import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Page } from './Page';
import { FcPrivacy } from 'react-icons/fc';

export const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('intro');

  const sections = [
    { id: 'intro', title: t('privacyPolicy.sections.intro.title') },
    { id: 'collect', title: t('privacyPolicy.sections.collect.title') },
    { id: 'use', title: t('privacyPolicy.sections.use.title') },
    { id: 'disclosure', title: t('privacyPolicy.sections.disclosure.title') },
    { id: 'security', title: t('privacyPolicy.sections.security.title') },
    { id: 'changes', title: t('privacyPolicy.sections.changes.title') },
    { id: 'contact', title: t('privacyPolicy.sections.contact.title') },
  ];

  return (
    <Page>
      <div className="max-w-5xl mx-auto px-6 py-16 mt-12 text-white">
        {/* Header Section */}
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-center mb-3 text-stroke">
            <FcPrivacy className="inline-block mr-4" />
            {t('privacyPolicy.title')}
          </h1>
          <p className="text-gray-200">{t('privacyPolicy.lastUpdated')}</p>
        </div>

        {/* Content Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-9">
          {/* Left Sidebar Navigation */}
          <div className="md:col-span-1 mt-10">
            <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-4 sticky top-24">
              <h2 className="font-bold text-lg mb-4 text-white">
                {t('privacyPolicy.contents')}
              </h2>
              <nav>
                <ul className="space-y-2">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => setActiveSection(section.id)}
                        className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                          activeSection === section.id
                            ? 'bg-purple-800/30 text-white font-medium backdrop-blur-sm border border-white/20'
                            : 'hover:bg-white/10 text-white backdrop-blur-sm'
                        }`}
                      >
                        {section.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="md:col-span-2">
            <div className="bg-transparent rounded-lg p-8">
              {/* Introduction */}
              <section
                id="intro"
                className={activeSection === 'intro' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('privacyPolicy.sections.intro.title')}
                </h2>
                <div className="bg-blue-50/30 backdrop-blur-sm border-l-4 border-blue-500 p-4 mb-6">
                  <p className="text-white">
                    {t('privacyPolicy.sections.intro.description')}
                  </p>
                </div>
              </section>

              {/* Information We Collect */}
              <section
                id="collect"
                className={activeSection === 'collect' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('privacyPolicy.sections.collect.title')}
                </h2>
                <p className="text-white mb-4">
                  {t('privacyPolicy.sections.collect.description')}
                </p>
                <div className="space-y-4">
                  <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                    <h3 className="text-lg font-semibold text-blue-300">
                      {t('privacyPolicy.sections.collect.personalData')}
                    </h3>
                    <p className="text-white mt-2">
                      {t('privacyPolicy.sections.collect.personalDataDesc')}
                    </p>
                  </div>

                  <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-4 hover:border-green-300 transition-colors">
                    <h3 className="text-lg font-semibold text-green-300">
                      {t('privacyPolicy.sections.collect.usageData')}
                    </h3>
                    <p className="text-white mt-2">
                      {t('privacyPolicy.sections.collect.usageDataDesc')}
                    </p>
                  </div>

                  <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors">
                    <h3 className="text-lg font-semibold text-purple-300">
                      {t('privacyPolicy.sections.collect.cookiesTracking')}
                    </h3>
                    <p className="text-white mt-2">
                      {t('privacyPolicy.sections.collect.cookiesTrackingDesc')}
                    </p>
                  </div>
                </div>
              </section>

              {/* How We Use Your Information */}
              <section
                id="use"
                className={activeSection === 'use' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('privacyPolicy.sections.use.title')}
                </h2>
                <p className="text-white mb-4">
                  {t('privacyPolicy.sections.use.description')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors flex items-start">
                    <div className="bg-blue-500/30 p-2 rounded-full mr-3 mt-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <p className="text-white">
                      {t('privacyPolicy.sections.use.provideService')}
                    </p>
                  </div>

                  <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors flex items-start">
                    <div className="bg-blue-500/30 p-2 rounded-full mr-3 mt-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <p className="text-white">
                      {t('privacyPolicy.sections.use.notifyChanges')}
                    </p>
                  </div>

                  <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors flex items-start">
                    <div className="bg-blue-500/30 p-2 rounded-full mr-3 mt-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <p className="text-white">
                      {t('privacyPolicy.sections.use.customerSupport')}
                    </p>
                  </div>

                  <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors flex items-start">
                    <div className="bg-blue-500/30 p-2 rounded-full mr-3 mt-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <p className="text-white">
                      {t('privacyPolicy.sections.use.gatherAnalysis')}
                    </p>
                  </div>

                  <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors flex items-start">
                    <div className="bg-blue-500/30 p-2 rounded-full mr-3 mt-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <p className="text-white">
                      {t('privacyPolicy.sections.use.monitorUsage')}
                    </p>
                  </div>

                  <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors flex items-start">
                    <div className="bg-blue-500/30 p-2 rounded-full mr-3 mt-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <p className="text-white">
                      {t('privacyPolicy.sections.use.technicalIssues')}
                    </p>
                  </div>
                </div>
              </section>

              {/* Disclosure */}
              <section
                id="disclosure"
                className={activeSection === 'disclosure' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('privacyPolicy.sections.disclosure.title')}
                </h2>
                <div className="bg-purple-50/20 backdrop-blur-sm border-l-4 border-purple-500 p-5 mb-6">
                  <p className="text-white">
                    {t('privacyPolicy.sections.disclosure.description')}
                  </p>
                </div>
                <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-5 mt-6">
                  <h3 className="text-lg font-semibold text-white mb-3">
                    {t('privacyPolicy.sections.disclosure.thirdPartyTitle')}
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="bg-purple-500/30 p-1 rounded-full mr-3 mt-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <p className="text-white text-sm">
                        {t(
                          'privacyPolicy.sections.disclosure.serviceProviders',
                        )}
                      </p>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-purple-500/30 p-1 rounded-full mr-3 mt-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <p className="text-white text-sm">
                        {t(
                          'privacyPolicy.sections.disclosure.legalRequirements',
                        )}
                      </p>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-purple-500/30 p-1 rounded-full mr-3 mt-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <p className="text-white text-sm">
                        {t(
                          'privacyPolicy.sections.disclosure.protectionRights',
                        )}
                      </p>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Security */}
              <section
                id="security"
                className={activeSection === 'security' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('privacyPolicy.sections.security.title')}
                </h2>
                <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-5 hover:border-green-300 transition-colors">
                  <div className="flex items-center mb-4">
                    <div className="bg-green-500/30 p-2 rounded-full mr-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-white">
                      {t('privacyPolicy.sections.security.securityMeasures')}
                    </h3>
                  </div>
                  <p className="text-white mb-4">
                    {t('privacyPolicy.sections.security.description')}
                  </p>
                  <div className="bg-yellow-50/30 backdrop-blur-sm border-l-4 border-yellow-400 p-4 mt-5">
                    <h4 className="font-semibold text-yellow-200 mb-2">
                      {t('privacyPolicy.sections.security.importantNote')}
                    </h4>
                    <p className="text-white text-sm">
                      {t('privacyPolicy.sections.security.noteDescription')}
                    </p>
                  </div>
                </div>
              </section>

              {/* Changes */}
              <section
                id="changes"
                className={activeSection === 'changes' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('privacyPolicy.sections.changes.title')}
                </h2>
                <p className="text-white mb-5">
                  {t('privacyPolicy.sections.changes.description')}
                </p>
                <div className="flex items-center bg-gray-50/30 backdrop-blur-sm p-4 rounded-lg border border-gray-200">
                  <div className="bg-blue-100/30 p-2 rounded-full mr-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-white text-sm">
                    {t('privacyPolicy.sections.changes.reviewEncourage')}
                  </p>
                </div>
              </section>

              {/* Contact */}
              <section
                id="contact"
                className={activeSection === 'contact' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('privacyPolicy.sections.contact.title')}
                </h2>
                <p className="text-white mb-6">
                  {t('privacyPolicy.sections.contact.description')}
                </p>
                <a
                  href="mailto:contact@betzen.app"
                  className="inline-flex items-center px-4 py-2 rounded-md hover:opacity-90 transition-opacity"
                  style={{
                    background: 'linear-gradient(90deg, #5f2c82, #49a09d)',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="text-white">contact@betzen.app</span>
                </a>
              </section>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
};
